import { useEffect, useState } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import { toast } from "sonner";
import { Globe, Users, MousePointerClick, Smartphone, Monitor, RefreshCcw } from "lucide-react";

const RANGE_OPTIONS = [
  { v: 7, label: "7 days" },
  { v: 30, label: "30 days" },
  { v: 90, label: "90 days" },
];

const DEVICE_COLORS = { desktop: "#1F4853", mobile: "#2D6E7C" };

export default function AdminWebsiteAnalytics() {
  const [data, setData] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [range, setRange] = useState(30);
  const [loading, setLoading] = useState(true);

  const load = async (days = range) => {
    setLoading(true);
    try {
      const [r, f] = await Promise.all([
        api.get(`/admin/analytics/website/summary?days=${days}`),
        api.get(`/admin/analytics/website/funnel?days=${days}`),
      ]);
      setData(r.data);
      setFunnel(f.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(range); /* eslint-disable-next-line */ }, [range]);

  return (
    <ToolPageShell
      eyebrow="Admin · Website Analytics"
      title="Public site traffic & conversion"
      intro="First-party visitor analytics — pageviews, sessions, top pages, referrers, UTM campaigns, and device split for noderahealth.com. No cookies, no PII."
    >
      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="analytics-range-bar">
        {RANGE_OPTIONS.map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => setRange(o.v)}
            data-testid={`analytics-range-${o.v}`}
            className={`text-xs font-bold rounded-full px-4 py-1.5 ${
              range === o.v
                ? "bg-[#1F4853] text-white"
                : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
          >
            {o.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => load(range)}
          data-testid="analytics-refresh-btn"
          className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-900"
        >
          <RefreshCcw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {!data ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="analytics-kpis">
            <Kpi label="Unique visitors" v={data.totals.unique_visitors} icon={<Users className="w-3.5 h-3.5" />} accent="bg-[#1F4853]" />
            <Kpi label="Sessions" v={data.totals.sessions} icon={<Globe className="w-3.5 h-3.5" />} accent="bg-[#2D6E7C]" />
            <Kpi label="Pageviews" v={data.totals.pageviews} icon={<MousePointerClick className="w-3.5 h-3.5" />} accent="bg-blue-700" />
            <Kpi label="Avg pages / session" v={
              data.totals.sessions
                ? (data.totals.pageviews / data.totals.sessions).toFixed(1)
                : "—"
            } icon={<Monitor className="w-3.5 h-3.5" />} accent="bg-indigo-600" />
          </div>

          <div className="grid lg:grid-cols-3 gap-5 mb-5">
            <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5" data-testid="analytics-timeseries">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">
                Visitors · last {data.days} days
              </div>
              {data.timeseries.length === 0 ? (
                <div className="text-sm text-slate-500 py-10 text-center italic">
                  No traffic yet — instrumentation is live; data will appear here as visitors arrive.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={data.timeseries}>
                    <defs>
                      <linearGradient id="grad-visitors" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#1F4853" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#1F4853" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="visitors" stroke="#1F4853" strokeWidth={2} fill="url(#grad-visitors)" name="Visitors" />
                    <Area type="monotone" dataKey="pageviews" stroke="#2D6E7C" strokeWidth={1.5} fillOpacity={0} name="Pageviews" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="analytics-device-split">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">
                Device split
              </div>
              {data.device_split.length === 0 ? (
                <div className="text-sm text-slate-500 py-10 text-center italic">No data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={data.device_split} dataKey="count" nameKey="device" innerRadius={40} outerRadius={80} paddingAngle={3}>
                      {data.device_split.map((d) => (
                        <Cell key={d.device} fill={DEVICE_COLORS[d.device] || "#94A3B8"} />
                      ))}
                    </Pie>
                    <Legend formatter={(v) => v.charAt(0).toUpperCase() + v.slice(1)} />
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-5 mb-5">
            <TopList label="Top pages" rows={data.top_pages} testId="analytics-top-pages" />
            <TopList label="Top referrers" rows={data.top_referrers} testId="analytics-top-referrers" />
            <TopList label="UTM sources" rows={data.top_utm_sources} testId="analytics-top-utm-sources" />
            <TopList label="UTM campaigns" rows={data.top_utm_campaigns} testId="analytics-top-utm-campaigns" />
          </div>

          {(data.top_events || []).length > 0 && (
            <TopList label="Top tracked events" rows={data.top_events.map(e => ({ value: e.name, count: e.count }))} testId="analytics-top-events" />
          )}

          <FunnelTable funnel={funnel} />

          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5" data-testid="analytics-recent">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">
              Last 30 raw pageviews
            </div>
            {data.recent.length === 0 ? (
              <div className="text-sm text-slate-500 italic">No data yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                      <th className="text-left font-bold py-2 pr-3">When</th>
                      <th className="text-left font-bold py-2 pr-3">Path</th>
                      <th className="text-left font-bold py-2 pr-3">Referrer</th>
                      <th className="text-left font-bold py-2 pr-3">UTM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100">
                        <td className="py-2 pr-3 text-xs text-slate-500 whitespace-nowrap">
                          {new Date(r.ts).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                        </td>
                        <td className="py-2 pr-3 font-mono text-xs text-slate-800">{r.path}</td>
                        <td className="py-2 pr-3 text-xs text-slate-500 truncate max-w-xs">{r.referrer || "—"}</td>
                        <td className="py-2 pr-3 text-xs text-slate-500">
                          {[r.utm_source, r.utm_medium, r.utm_campaign].filter(Boolean).join(" / ") || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </ToolPageShell>
  );
}

function Kpi({ label, v, accent, icon }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold text-white ${accent} px-2 py-0.5 rounded`}>
        {icon} {label}
      </div>
      <div className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900">{v}</div>
    </div>
  );
}

function TopList({ label, rows, testId }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">
        {label}
      </div>
      {(rows || []).length === 0 ? (
        <div className="text-sm text-slate-500 italic">No data yet</div>
      ) : (
        <ul>
          {rows.map((r, i) => (
            <li key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
              <span className="text-sm text-slate-800 truncate max-w-[70%]" title={r.value}>{r.value}</span>
              <span className="text-sm font-bold text-slate-900 tabular-nums">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Conversion funnel per UTM source — sessions → demo / pricing-CTA /
 * newsletter conversion rate. "direct" bucket gathers untagged traffic.
 */
function FunnelTable({ funnel }) {
  if (!funnel) return null;
  const rows = funnel.rows || [];
  return (
    <div
      className="mt-5 rounded-2xl border border-slate-200 bg-white p-5"
      data-testid="analytics-funnel"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">
            Conversion funnel · per UTM source
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Sessions → any conversion event ({" "}
            <span className="font-mono text-[10px]">demo_form_submitted</span>,{" "}
            <span className="font-mono text-[10px]">pricing_book_demo_cta_clicked</span>,{" "}
            <span className="font-mono text-[10px]">newsletter_subscribed</span>{" "}
            ). Source is the FIRST pageview's <code>utm_source</code> per session.
          </div>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-6 text-center">
          No traffic with a converting session yet. Drive a UTM-tagged visitor
          and fire one of the 3 conversion events to populate this.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left font-bold py-2 pr-3">Source</th>
                <th className="text-right font-bold py-2 pr-3">Sessions</th>
                <th className="text-right font-bold py-2 pr-3">Converted</th>
                <th className="text-right font-bold py-2 pr-3">Rate</th>
                <th className="text-left font-bold py-2 pr-3">
                  <span className="text-emerald-700">Demo</span> ·{" "}
                  <span className="text-blue-700">CTA</span> ·{" "}
                  <span className="text-amber-700">News</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const rate = (r.rate || 0) * 100;
                const bar = Math.min(100, Math.max(0, rate));
                const tier =
                  rate >= 10 ? "bg-emerald-500"
                  : rate >= 3 ? "bg-amber-500"
                  : rate >= 0.5 ? "bg-blue-500"
                  : "bg-slate-300";
                return (
                  <tr
                    key={i}
                    className="border-b border-slate-100"
                    data-testid={`analytics-funnel-row-${r.utm_source}`}
                  >
                    <td className="py-3 pr-3 font-semibold text-slate-900">
                      {r.utm_source}
                    </td>
                    <td className="py-3 pr-3 text-right tabular-nums">{r.sessions}</td>
                    <td className="py-3 pr-3 text-right tabular-nums font-bold">
                      {r.converted_sessions}
                    </td>
                    <td className="py-3 pr-3 text-right">
                      <div className="inline-flex items-center gap-2 justify-end w-full">
                        <div className="hidden sm:block h-1.5 w-20 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${tier}`} style={{ width: `${bar}%` }} />
                        </div>
                        <span className="tabular-nums font-bold text-slate-900">
                          {rate.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-xs text-slate-600">
                      <span className="text-emerald-700 font-bold">
                        {r.by_event?.demo_form_submitted || 0}
                      </span>
                      <span className="mx-1 text-slate-300">·</span>
                      <span className="text-blue-700 font-bold">
                        {r.by_event?.pricing_book_demo_cta_clicked || 0}
                      </span>
                      <span className="mx-1 text-slate-300">·</span>
                      <span className="text-amber-700 font-bold">
                        {r.by_event?.newsletter_subscribed || 0}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

